// TelcoTrack - Notification Service
// Inter-role notifications and toast-style feedback

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoggerService } from './logger.service';
import type { Notification } from '../models/notification.model';
import { ToastService } from '../../shared/components/ui/toast/toast.service';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  /** Timeout for notification API so 504/slow workflow-service does not block UI */
  private readonly notificationRequestTimeoutMs = 10_000;
  private readonly apiUrl = `${environment.apiUrl}${environment.notificationsServiceUrl}`;
  private readonly _notifications$ = new BehaviorSubject<Notification[]>([]);
  private readonly _unreadCount$ = new BehaviorSubject<number>(0);

  constructor(
    private readonly http: HttpClient,
    private readonly logger: LoggerService,
    private readonly toast: ToastService,
  ) {}

  /**
   * Get notifications for current user
   */
  getNotifications(filter: 'all' | 'unread' = 'all'): Observable<Notification[]> {
    const params = filter === 'unread' ? { filter } : {};
    return this.http.get<ApiResponse<Notification[]>>(this.apiUrl, { params: params as any, withCredentials: true }).pipe(
      timeout(this.notificationRequestTimeoutMs),
      map(res => {
        const raw = res?.success && Array.isArray(res.data) ? res.data : [];
        return raw.map((n: any) => ({
          id: n.id,
          userId: n.userId ?? n.user_id,
          objectId: n.objectId ?? n.object_id,
          objectType: n.objectType ?? n.object_type,
          phaseCode: n.phaseCode ?? n.phase_code,
          message: n.message,
          readAt: n.readAt ?? n.read_at ?? null,
          createdAt: n.createdAt ?? n.created_at,
        })) as Notification[];
      }),
      tap(list => this._notifications$.next(list)),
      catchError(err => {
        this.logger.warn('Failed to load notifications', err);
        return of([]);
      })
    );
  }

  /**
   * Get unread notification count
   */
  getUnreadCount(): Observable<number> {
    return this.http.get<ApiResponse<{ count: number }>>(`${this.apiUrl}/unread-count`, { withCredentials: true }).pipe(
      timeout(this.notificationRequestTimeoutMs),
      map(res => (res?.success && typeof res.data?.count === 'number' ? res.data.count : 0)),
      tap(count => this._unreadCount$.next(count)),
      catchError(err => {
        this.logger.warn('Failed to get unread count', err);
        return of(0);
      })
    );
  }

  /**
   * Mark a notification as read
   */
  markAsRead(notificationId: string): Observable<void> {
    return this.http.put<ApiResponse<unknown>>(`${this.apiUrl}/${notificationId}/read`, {}, { withCredentials: true }).pipe(
      tap(() => {
        const list = this._notifications$.value.map((n: Notification) => n.id === notificationId ? { ...n, readAt: new Date() } : n);
        this._notifications$.next(list);
        const count = Math.max(0, this._unreadCount$.value - 1);
        this._unreadCount$.next(count);
      }),
      map(() => undefined),
      catchError(err => {
        this.logger.warn('Failed to mark notification as read', err);
        throw err;
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<void> {
    return this.http.put<ApiResponse<unknown>>(`${this.apiUrl}/read-all`, {}, { withCredentials: true }).pipe(
      tap(() => {
        const list = this._notifications$.value.map((n: Notification) => ({ ...n, readAt: n.readAt ?? new Date() }));
        this._notifications$.next(list);
        this._unreadCount$.next(0);
      }),
      map(() => undefined),
      catchError(err => {
        this.logger.warn('Failed to mark all as read', err);
        throw err;
      })
    );
  }

  /** Observable of current notifications list */
  get notifications$(): Observable<Notification[]> {
    return this._notifications$.asObservable();
  }

  /** Observable of current unread count */
  get unreadCount$(): Observable<number> {
    return this._unreadCount$.asObservable();
  }

  /** Refresh notifications and unread count (e.g. after polling) */
  refresh(): void {
    this.getNotifications('all').subscribe();
    this.getUnreadCount().subscribe();
  }

  /** Success toast. Message is shown as passed. */
  showSuccess(message: string): void {
    this.toast.showSuccess(message);
  }

  /** Error toast. Message is shown as passed. */
  showError(message: string): void {
    this.toast.showError(message);
  }

  /** Info toast. Message is shown as passed. */
  showInfo(message: string): void {
    this.toast.showInfo(message);
  }
}
